/**
 * useWorkspaceConfig
 *
 * Persiste em localStorage quais tags de acao aparecem no AI Workspace.
 * Defaults (operador novo, sem config salva): Traduzir, Resposta Rapida,
 * Chamar Agente e Mensagens Prontas ja ligados — sao os mais usados no
 * dia-a-dia. Etiqueta, Followup e Comentario comecam desligados no
 * workspace porque continuam acessiveis pelo menu de 3 pontinhos do chat.
 *
 * Por que localStorage e nao backend: o usuario pediu carregamento
 * instantaneo do workspace, sem round-trip para o servidor. Como e
 * preferencia visual por dispositivo, perder a config ao trocar de browser
 * e aceitavel — sempre cai no default sensato.
 */
import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'infinitsales:ai_workspace_config_v1';

// Chaves canonicas das acoes disponiveis no workspace.
export const WORKSPACE_ACTION_KEYS = {
  TRANSLATE: 'translate',
  GENERATE: 'generate',
  CUSTOM: 'custom',          // Chamar o Agente
  SAVED: 'saved_messages',   // Mensagens Prontas (salvas + templates Meta)
  LABEL: 'label',
  FOLLOWUP: 'followup',
  FEEDBACK: 'feedback',      // Adicionar comentario
};

// Prefixo das chaves DINAMICAS de roleta de transferencia: 'roulette:<id>'.
// Sao grupos criados pelo admin (nao ha lista fixa), por isso nao entram no
// DEFAULT_CONFIG — o operador fixa/desfixa cada grupo igual as acoes fixas.
// Default: nao-fixado (so aparece no Configurar Workspace ate ser fixado).
export const ROULETTE_KEY_PREFIX = 'roulette:';
export const rouletteKey = (groupId) => `${ROULETTE_KEY_PREFIX}${groupId}`;
const isDynamicKey = (key) =>
  typeof key === 'string' && key.startsWith(ROULETTE_KEY_PREFIX);

// Acoes que sempre aparecem no menu de 3 pontinhos do chat, mesmo quando
// desligadas no workspace. Servem como fallback garantido para o operador.
export const ALWAYS_IN_MENU = new Set([
  WORKSPACE_ACTION_KEYS.CUSTOM,
  WORKSPACE_ACTION_KEYS.SAVED,
  WORKSPACE_ACTION_KEYS.LABEL,
  WORKSPACE_ACTION_KEYS.FOLLOWUP,
  WORKSPACE_ACTION_KEYS.FEEDBACK,
]);

const DEFAULT_CONFIG = {
  [WORKSPACE_ACTION_KEYS.TRANSLATE]: true,
  [WORKSPACE_ACTION_KEYS.GENERATE]: true,
  [WORKSPACE_ACTION_KEYS.CUSTOM]: true,
  [WORKSPACE_ACTION_KEYS.SAVED]: true,
  [WORKSPACE_ACTION_KEYS.LABEL]: false,
  [WORKSPACE_ACTION_KEYS.FOLLOWUP]: false,
  [WORKSPACE_ACTION_KEYS.FEEDBACK]: false,
};

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {
    // Storage corrompido — ignora e cai no default.
  }
  return null;
}

export function useWorkspaceConfig() {
  const [config, setConfig] = useState(() => {
    const stored = readStorage();
    return stored ? { ...DEFAULT_CONFIG, ...stored } : { ...DEFAULT_CONFIG };
  });

  // Persistencia: salva sempre que muda. Falhas (quota cheia, modo privado
  // restritivo) sao silenciadas porque a config segue valendo em memoria.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch {
      // ignore
    }
  }, [config]);

  // Sincroniza entre abas/janelas do mesmo navegador — operador que muda
  // a config em uma aba ve refletido nas outras sem reload.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      try {
        const parsed = JSON.parse(e.newValue);
        if (parsed && typeof parsed === 'object') {
          setConfig({ ...DEFAULT_CONFIG, ...parsed });
        }
      } catch {}
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const toggle = useCallback((key) => {
    if (!(key in DEFAULT_CONFIG) && !isDynamicKey(key)) return;
    setConfig((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const setEnabled = useCallback((key, enabled) => {
    if (!(key in DEFAULT_CONFIG) && !isDynamicKey(key)) return;
    setConfig((prev) => ({ ...prev, [key]: !!enabled }));
  }, []);

  const isEnabled = useCallback((key) => !!config[key], [config]);

  return { config, toggle, setEnabled, isEnabled };
}

export default useWorkspaceConfig;
